import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import React from "react";

import { BlankButton, PrimaryButton } from "../../components";
import { useExportContext } from "../../contexts/ExportContext";
import { useModalContext } from "../../contexts/ModalContext";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";

import {
  DataExportFile,
  dataExportProgressSlice,
} from "../../redux/slices/dataExportProgressSlice";
import { dataExportSlice } from "../../redux/slices/dataExportSlice";
import { emptyModalState } from "../../shared/Constants";
import { MovementType } from "../../types";

export const DataExportModal: React.FC = (): JSX.Element => {
  const { setModalState } = useModalContext();
  const { setExportProgressState, setExportInProgress } = useExportContext();

  // Redux calls
  const dataExportState = useAppSelector((state) => state.dataExport);
  const orgData = useAppSelector((state) => state.org);
  const dataExportFiles = useAppSelector(
    (state) => state.dataExportProgress.files,
  );

  // RTK slice actions
  const dispatch = useAppDispatch();
  const { storeDataExport, clearDataExport } = dataExportSlice.actions;
  const { storeDataExportFiles } = dataExportProgressSlice.actions;

  const dataTypes = [
    {
      id: "inverse-kinematics",
      value: "Inverse Kinematics",
      label: "Inverse Kinematics Files",
    },
    {
      id: "momentum-energy",
      value: "Momentum",
      label: "Momentum Files",
    },
    { id: "metadata", value: "Metadata", label: "Metadata" },
  ];

  const movementTypes = orgData.details.movementTypes.map(
    (movement: MovementType) => {
      return { id: `${movement.id}`, value: movement.name };
    },
  );

  const handleMovementTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (
      dataExportState.movementTypes.some(
        (movement: { id: string; value: string }) =>
          movement.id === e.target.id,
      )
    ) {
      const updatedMovementTypes = dataExportState.movementTypes.filter(
        (m: { id: string; value: string }) => m.id !== e.target.id,
      );
      dispatch(
        storeDataExport({
          ...dataExportState,
          movementTypes: updatedMovementTypes,
        }),
      );
    } else {
      dispatch(
        storeDataExport({
          ...dataExportState,
          movementTypes: [
            ...dataExportState.movementTypes,
            { id: e.target.id, value: e.target.value },
          ],
        }),
      );
    }
  };

  const handleDataTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (
      dataExportState.dataTypes.some(
        (data: { value: string; id: string }) => data.value === e.target.value,
      )
    ) {
      const updatedDataTypes = dataExportState.dataTypes.filter(
        (d: { value: string; id: string }) => d.value !== e.target.value,
      );
      dispatch(
        storeDataExport({
          ...dataExportState,
          dataTypes: updatedDataTypes,
        }),
      );
    } else {
      dispatch(
        storeDataExport({
          ...dataExportState,
          dataTypes: [
            ...dataExportState.dataTypes,
            { value: e.target.value, id: e.target.id },
          ],
        }),
      );
    }
  };

  const openProgressModal = () => {
    const filesToExport: DataExportFile[] = [];

    dataExportState.sessionList.forEach(
      (session: { id: string; date: string }) => {
        if (
          dataExportState.movementTypes.length === 1 &&
          dataExportState.dataTypes.length === 1
        ) {
          const newFile = {
            orgPlayerId: dataExportState.orgPlayerId,
            sessionId: session.id,
            movementType: dataExportState.movementTypes[0],
            dataType: dataExportState.dataTypes[0],
            dataFormat: dataExportState.dataFormat,
            sessionDate: session.date,
            playerName: dataExportState.playerName,
          };
          filesToExport.push(newFile);
        } else {
          dataExportState.movementTypes.forEach(
            (movementType: { id: string; value: string }) => {
              dataExportState.dataTypes.forEach(
                (dataType: { value: string; id: string }) => {
                  const newFile = {
                    orgPlayerId: dataExportState.orgPlayerId,
                    sessionId: session.id,
                    movementType: movementType,
                    dataType: dataType,
                    dataFormat: dataExportState.dataFormat,
                    sessionDate: session.date,
                    playerName: dataExportState.playerName,
                  };
                  filesToExport.push(newFile);
                },
              );
            },
          );
        }
      },
    );

    let numberOfFiles: number;

    if (dataExportFiles.length > 0) {
      const existingAndNewFiles = filesToExport.concat([...dataExportFiles]);
      const filesWithoutAStatus = existingAndNewFiles.filter(
        (file) => !file.status,
      );
      numberOfFiles = filesWithoutAStatus.length;
      dispatch(storeDataExportFiles({ files: existingAndNewFiles }));
    } else {
      numberOfFiles = filesToExport.length;
      dispatch(storeDataExportFiles({ files: filesToExport }));
    }

    const filesSingularOrPlural = numberOfFiles === 1 ? "Export" : "Exports";
    setExportProgressState({
      shouldOpen: true,
      title: `Preparing ${numberOfFiles} ${filesSingularOrPlural}`,
    });
    sessionStorage.setItem("showExportProgress", "true");
    sessionStorage.setItem(
      "exportProgressTitle",
      `Preparing ${numberOfFiles} ${filesSingularOrPlural}`,
    );

    setModalState(emptyModalState);
    setExportInProgress(true);
    dispatch(clearDataExport());
  };

  return (
    <div className="data-export-container">
      <div
        className="bg-[#F9FAFB] flex justify-center p-4 space-x-40 max-h-52 overflow-scroll"
        data-testid="data-export-header"
      >
        <div className="space-y-3 text-sm leading-4">
          <p className="text-gray-500" data-testid="data-export-player-header">
            Player
          </p>
          <p data-testid="data-export-player">{dataExportState.playerName}</p>
        </div>
        <div className="space-y-3 text-sm leading-4">
          <p
            className="text-gray-500"
            data-testid="data-export-session-id-header"
          >
            Session ID
          </p>
          {dataExportState.sessionList.map(
            (session: { id: string; date: string }) => (
              <div className="flex" key={session.id}>
                <p data-testid="data-export-session-id">{session.id}</p>
              </div>
            ),
          )}
        </div>
        <div className="space-y-3 text-sm leading-4">
          <p
            className="text-gray-500"
            data-testid="data-export-session-date-header"
          >
            Session Date
          </p>
          {dataExportState.sessionList.map(
            (session: { id: string; date: string }) => (
              <div className="flex" key={session.id}>
                <p data-testid="data-export-session-date">{session.date}</p>
              </div>
            ),
          )}
        </div>
      </div>

      <div
        className="p-8 flex justify-center space-x-40"
        data-testid="data-export-options"
      >
        <div
          className="flex flex-col space-y-3"
          data-testid="movement-type-checkboxes"
        >
          <p
            className="text-gray-800 text-lg"
            data-testid="data-export-movement-type-header"
          >
            Select Movement Type
          </p>
          {movementTypes.map((movement: { id: string; value: string }) => (
            <div
              className="text-gray-600 flex items-center"
              key={movement.id}
              data-testid="data-export-movement-type"
            >
              <input
                type="checkbox"
                className="border-gray-300 rounded-[4px] mr-4 checked:bg-[#FB6A07] checked:focus:bg-[#FB6A07] checked:hover:bg-[#FB6A07] focus:ring-[#FB6A07] focus:ring-opacity-50 focus:ring-2"
                onChange={handleMovementTypeChange}
                value={movement.value}
                id={movement.id}
                data-testid="movement-type-checkbox"
                checked={dataExportState.movementTypes.some(
                  (type: { id: string; value: string }) =>
                    type.id === movement.id,
                )}
              />
              <label className="text-sm">{movement.value}</label>
            </div>
          ))}
        </div>
        <div
          className="flex flex-col space-y-3"
          data-testid="data-type-checkboxes"
        >
          <p
            className="text-gray-800 text-lg"
            data-testid="data-export-data-type-header"
          >
            Select Data Type
          </p>
          {dataTypes.map(
            (
              data: { id: string; value: string; label: string },
              index: number,
            ) => (
              <div
                className="text-gray-600 flex items-center"
                key={index}
                data-testid="data-export-data-type"
              >
                <input
                  type="checkbox"
                  className="border-gray-300 rounded-[4px] mr-4 checked:bg-[#FB6A07] checked:focus:bg-[#FB6A07] checked:hover:bg-[#FB6A07] focus:ring-[#FB6A07] focus:ring-opacity-50 focus:ring-2"
                  onChange={handleDataTypeChange}
                  id={data.id}
                  value={data.value}
                  data-testid="data-type-checkbox"
                  checked={dataExportState.dataTypes.some(
                    (type: { value: string; id: string }) =>
                      type.value === data.value,
                  )}
                />
                <label className="text-sm">{data.label}</label>
              </div>
            ),
          )}
        </div>
      </div>

      <footer
        data-testid="data-export-footer"
        className="flex justify-end mb-5 pt-5 pr-5 w-full border-t border-gray-200"
      >
        <BlankButton
          data-testid="cancel-export-button"
          type="button"
          onClick={() => {
            setModalState(emptyModalState);
            dispatch(clearDataExport());
          }}
        >
          Cancel
        </BlankButton>
        <PrimaryButton
          data-testid="prepare-export-button"
          className="ml-3 flex"
          onClick={openProgressModal}
          disabled={
            dataExportState.movementTypes.length === 0 ||
            dataExportState.dataTypes.length === 0
          }
        >
          <ArrowDownTrayIcon className="h-5 w-5 mr-2 stroke-2" />
          Export Data
        </PrimaryButton>
      </footer>
    </div>
  );
};
